import { useEffect, useState } from 'react';

import {
  ArrowLeft,
  CheckCircle,
  CheckCircle2,
  ChevronDown,
  Clock,
  Eye,
  Loader2,
  Phone,
  TrendingUp,
} from 'lucide-react';
import { Link, useParams } from 'react-router-dom';

import { useOutcomeDisplay } from '@/components/calls/OutcomeBadge';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useToast } from '@/hooks/use-toast';
import { useUpdateHumanReviewed } from '@/hooks/useCalls';
import { useCampaign } from '@/hooks/useCampaigns';
import { usePageTitle } from '@/hooks/usePageTitle';
import { useStartReview, useCampaignReview, useCampaignReviews } from '@/hooks/useReviews';
import { useTools } from '@/hooks/useTools';
import { getEnv } from '@/lib/env';
import { queryClient } from '@/main';
import { calculateAvailabilityHours } from '@/utils/availability';
import { formatDate, formatDateTime } from '@/utils/format';
import { convertUTCToLocalTime, getTimezoneAbbreviation } from '@/utils/timezone';
import { getToolLabel } from '@/utils/tools';

import { CALL_DIRECTION_BADGES, CALL_STATUS_BADGES } from '@repo/shared-types';

import type { CallRecord, CampaignStatus } from '@repo/shared-types';

/**
 * Calculate campaign statistics from calls.
 * Merged from availability-hours branch.
 */
function calculateCampaignStats(calls: CallRecord[] | undefined) {
  if (!calls || calls.length === 0) {
    return { totalCalls: 0, availabilityCaptured: 0, totalHours: 0 };
  }

  const availabilityCalls = calls.filter((call) => call.outcome === 'AVAILABILITY_CAPTURED');
  const totalHours = availabilityCalls.reduce((sum, call) => {
    return sum + calculateAvailabilityHours(call.availability_data);
  }, 0);

  return {
    totalCalls: calls.length,
    availabilityCaptured: availabilityCalls.length,
    totalHours,
  };
}

const STATUS_BADGE_STYLES: Record<CampaignStatus, string> = {
  DRAFT: 'bg-gray-100 text-gray-800',
  ACTIVE: 'bg-emerald-100 text-emerald-800',
  FINISHED: 'bg-slate-200 text-slate-800',
  STOPPED: 'bg-rose-100 text-rose-800',
};

export const CampaignDetailPage = () => {
  usePageTitle('Campaign Details');
  const { id } = useParams<{ id: string }>();
  const { data: campaign, isLoading, error } = useCampaign(id);
  const { data: tools = [] } = useTools();
  const { mutate: startReview, isPending: isStartingReview } = useStartReview();
  const { toast } = useToast();
  const outcomeDisplay = useOutcomeDisplay();
  const [reviewId, setReviewId] = useState<string | null>(null);
  const [pendingUnreviewedOnly, setPendingUnreviewedOnly] = useState(false);
  // Get latest review for this campaign
  const { data: reviews } = useCampaignReviews(id);
  const latestReview = reviews?.[0];
  // Poll the active review if there is one
  const shouldPoll =
    reviewId || latestReview?.status === 'IN_PROGRESS' || latestReview?.status === 'PENDING';
  const { data: activeReview } = useCampaignReview(
    shouldPoll ? reviewId || latestReview?.id : undefined,
  );
  const updateHumanReviewedMutation = useUpdateHumanReviewed(id);

  // Auto-navigate when review completes
  useEffect(() => {
    if (activeReview?.status === 'COMPLETED' && reviewId) {
      toast({
        title: 'Review Completed',
        description: 'Click "View Results" to see the detailed analysis.',
      });
      queryClient.invalidateQueries({ queryKey: ['campaign-reviews', id] });
      // Use setTimeout to avoid setState during render
      setTimeout(() => setReviewId(null), 0);
    }
  }, [activeReview?.status, reviewId, toast, id]);

  const handleStartReview = (unreviewedOnly: boolean) => {
    if (!id) return;
    setPendingUnreviewedOnly(unreviewedOnly);
    handleConfirmStartReview(unreviewedOnly);
  };

  const handleConfirmStartReview = (unreviewedOnly?: boolean) => {
    if (!id) return;
    const isUnreviewedOnly = unreviewedOnly ?? pendingUnreviewedOnly;

    startReview(
      {
        campaignId: id,
        unreviewedOnly: isUnreviewedOnly,
      },
      {
        onSuccess: (data) => {
          setReviewId(data.reviewId);
          toast({
            title: 'Review Started',
            description: isUnreviewedOnly
              ? 'AI review process has been initiated for unreviewed calls only.'
              : 'AI review process has been initiated for all calls.',
          });
        },
      },
    );
  };

  if (isLoading) {
    return (
      <div className="flex min-h-100 items-center justify-center">
        <div className="text-center">
          <div className="border-primary mx-auto h-12 w-12 animate-spin rounded-full border-b-2" />
          <p className="text-muted-foreground mt-4">Loading campaign...</p>
        </div>
      </div>
    );
  }

  if (error || !campaign) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-muted-foreground text-sm tracking-[0.3em] uppercase">
              Campaign detail
            </p>
            <h1 className="text-3xl font-bold tracking-tight">Not Found</h1>
            <p className="text-muted-foreground">We could not locate campaign ID {id}.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <header className="flex items-center justify-between">
        <div className="space-y-2">
          <p className="text-muted-foreground text-sm tracking-[0.3em] uppercase">
            Campaign detail
          </p>
          <h1 className="text-3xl font-bold tracking-tight">{campaign.name}</h1>
          <p className="text-muted-foreground">Created {formatDate(campaign.created_at)}</p>
        </div>
        <div className="flex gap-2">
          {campaign.calls && campaign.calls.length > 0 && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button className="cursor-pointer" variant="default" disabled={isStartingReview}>
                  {isStartingReview ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Starting...
                    </>
                  ) : (
                    <>
                      <CheckCircle2 className="mr-2 h-4 w-4" />
                      Start AI Review
                      <ChevronDown className="ml-2 h-4 w-4" />
                    </>
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onSelect={() => handleStartReview(false)}>
                  Review All Calls
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={() => handleStartReview(true)}>
                  Review Unreviewed Only
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}

          <Button variant="outline" asChild>
            <Link to="/campaigns">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to campaigns
            </Link>
          </Button>
        </div>
      </header>

      {/* Review Status */}
      {latestReview && latestReview.status !== 'COMPLETED' && latestReview.status !== 'FAILED' && (
        <Card className="border-blue-200 bg-blue-50">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Loader2 className="h-5 w-5 animate-spin text-blue-600" />
                <div>
                  <p className="font-semibold text-blue-900">Review in progress...</p>
                  <p className="text-sm text-blue-700">
                    {activeReview?.reviewed_calls ?? latestReview.reviewed_calls ?? 0} of{' '}
                    {latestReview.total_calls || 0} calls reviewed
                  </p>
                </div>
              </div>
              <Badge className="bg-blue-100 text-blue-800">
                {latestReview.total_calls > 0
                  ? Math.round(
                      ((activeReview?.reviewed_calls ?? latestReview.reviewed_calls ?? 0) /
                        latestReview.total_calls) *
                        100,
                    )
                  : 0}
                % Complete
              </Badge>
            </div>
          </CardContent>
        </Card>
      )}

      {latestReview?.status === 'COMPLETED' && (
        <Card className="border-emerald-200 bg-emerald-50">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                <div>
                  <p className="font-semibold text-emerald-900">Review completed!</p>
                  <p className="text-sm text-emerald-700">
                    {latestReview.reviewed_calls || 0} calls analyzed •{' '}
                    {latestReview.calls_with_issues || 0} issues found
                  </p>
                </div>
              </div>
              <div className="flex gap-2">
                <Button size="sm" asChild>
                  <Link to={`/reviews/campaign/${id}/history`}>
                    <TrendingUp className="mr-2 h-4 w-4" />
                    View Results
                  </Link>
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Status</CardTitle>
            <CardDescription>Current campaign status</CardDescription>
          </CardHeader>
          <CardContent>
            <span
              className={`inline-flex rounded-full px-3 py-1 text-sm font-medium ${STATUS_BADGE_STYLES[campaign.status]}`}
            >
              {campaign.status}
            </span>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Runs at</CardTitle>
            <CardDescription>Scheduled execution date</CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-sm font-medium">{formatDate(campaign.runsAt)}</p>
          </CardContent>
        </Card>
      </div>

      {/* Campaign Statistics */}
      {(() => {
        const stats = calculateCampaignStats(campaign.calls);
        return (
          <div className="grid gap-4 md:grid-cols-3">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="rounded-full bg-blue-100 p-3 dark:bg-blue-950">
                    <Phone className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{stats.totalCalls}</p>
                    <p className="text-muted-foreground text-sm">Total Calls</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            {getEnv('VITE_CLIENT_COMPANY_NAME_INTERNAL') === 'NEW_CROSS' && (
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center gap-4">
                    <div className="rounded-full bg-emerald-100 p-3 dark:bg-emerald-950">
                      <CheckCircle className="h-6 w-6 text-emerald-600 dark:text-emerald-400" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold">{stats.availabilityCaptured}</p>
                      <p className="text-muted-foreground text-sm">Availability Captured</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
            {getEnv('VITE_CLIENT_COMPANY_NAME_INTERNAL') === 'NEW_CROSS' && (
              <Card className="border-emerald-200 bg-emerald-50/50 dark:border-emerald-800 dark:bg-emerald-950/20">
                <CardContent className="pt-6">
                  <div className="flex items-center gap-4">
                    <div className="rounded-full bg-emerald-200 p-3 dark:bg-emerald-900">
                      <Clock className="h-6 w-6 text-emerald-700 dark:text-emerald-300" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-emerald-700 dark:text-emerald-300">
                        {campaign.totalAvailabilityHours ?? stats.totalHours}
                      </p>
                      <p className="text-sm text-emerald-600 dark:text-emerald-400">
                        Total Hours Captured
                        {campaign.availabilityHoursSource &&
                          (campaign.availabilityHoursSource === 'tool'
                            ? ' — uploaded by tool'
                            : ' — from AI review')}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        );
      })()}

      <Card>
        <CardHeader>
          <CardTitle>Campaign metadata</CardTitle>
          <CardDescription>Basic information about this campaign</CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4 md:grid-cols-3">
          <div>
            <p className="text-muted-foreground text-sm">Name</p>
            <p className="text-sm font-medium">{campaign.name}</p>
          </div>
          <div>
            <p className="text-muted-foreground text-sm">Number of contacts</p>
            <p className="text-sm font-medium">{campaign.contacts.length}</p>
          </div>
          <div>
            <p className="text-muted-foreground text-sm">Created at</p>
            <p className="text-sm font-medium">{formatDateTime(campaign.created_at)}</p>
          </div>
        </CardContent>
      </Card>

      {campaign.configuration && (
        <Card>
          <CardHeader>
            <CardTitle>Configuration details</CardTitle>
            <CardDescription>Full configuration settings for this campaign</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <p className="text-muted-foreground text-sm">Name</p>
                <p className="text-sm font-medium">{campaign.configuration.name}</p>
              </div>
              <div>
                <p className="text-muted-foreground text-sm">Call window</p>
                <p className="text-sm font-medium">
                  {convertUTCToLocalTime(campaign.configuration.call_window_start)} -{' '}
                  {convertUTCToLocalTime(campaign.configuration.call_window_end)}{' '}
                  <span className="text-muted-foreground">({getTimezoneAbbreviation()})</span>
                </p>
              </div>
              <div>
                <p className="text-muted-foreground text-sm">Retry limit</p>
                <p className="text-sm font-medium">{campaign.configuration.retry_limit}</p>
              </div>
              <div>
                <p className="text-muted-foreground text-sm">Retry delay</p>
                <p className="text-sm font-medium">
                  {campaign.configuration.retry_delay_minutes} minutes
                </p>
              </div>
              {campaign.configuration.provider && (
                <div>
                  <p className="text-muted-foreground text-sm">Provider</p>
                  <p className="text-sm font-medium">
                    {campaign.configuration.provider === 'gemini'
                      ? 'Google Gemini'
                      : 'OpenAI Realtime'}
                  </p>
                </div>
              )}
              {campaign.configuration.voice_name && (
                <div>
                  <p className="text-muted-foreground text-sm">Voice</p>
                  <p className="text-sm font-medium">{campaign.configuration.voice_name}</p>
                </div>
              )}
              {campaign.configuration.llm_model && (
                <div>
                  <p className="text-muted-foreground text-sm">LLM Model</p>
                  <p className="text-sm font-medium">{campaign.configuration.llm_model}</p>
                </div>
              )}
            </div>
            {campaign.configuration.tools && campaign.configuration.tools.length > 0 && (
              <div>
                <p className="text-muted-foreground mb-2 text-sm">Tools</p>
                <div className="flex flex-wrap gap-1.5">
                  {campaign.configuration.tools.map((tool) => (
                    <span
                      key={tool}
                      className="bg-primary/10 text-primary inline-flex items-center rounded-md px-2 py-1 text-xs font-medium"
                    >
                      {getToolLabel(tool, tools)}
                    </span>
                  ))}
                </div>
              </div>
            )}
            <div>
              <p className="text-muted-foreground mb-2 text-sm">Prompt template</p>
              <div className="border-input bg-muted/50 rounded-md border p-3 text-sm">
                <p className="whitespace-pre-wrap">
                  {campaign.configuration.active_version?.prompt_template || 'No prompt defined'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Contacts</CardTitle>
          <CardDescription>
            {campaign.contacts.length} contact{campaign.contacts.length !== 1 ? 's' : ''} in this
            campaign
          </CardDescription>
        </CardHeader>
        <CardContent>
          {campaign.contacts.length === 0 ? (
            <div className="text-muted-foreground rounded-lg border border-dashed p-6 text-center text-sm">
              No contacts added to this campaign yet.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="text-muted-foreground text-left text-sm">
                    <th className="border-b px-3 py-2">Name</th>
                    <th className="border-b px-3 py-2">Phone number</th>
                  </tr>
                </thead>
                <tbody>
                  {campaign.contacts.map((contact) => (
                    <tr key={contact.id} className="text-sm">
                      <td className="border-b px-3 py-2 font-medium">{contact.name}</td>
                      <td className="border-b px-3 py-2">{contact.phone}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Calls</CardTitle>
          <CardDescription>
            {campaign.calls?.length || 0} call{(campaign.calls?.length || 0) !== 1 ? 's' : ''} made
            for this campaign
          </CardDescription>
        </CardHeader>
        <CardContent>
          {!campaign.calls || campaign.calls.length === 0 ? (
            <div className="text-muted-foreground rounded-lg border border-dashed p-6 text-center text-sm">
              No calls have been made for this campaign yet.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="text-muted-foreground text-left text-sm">
                    <th className="border-b px-3 py-2">Contact</th>
                    <th className="border-b px-3 py-2">Direction</th>
                    <th className="border-b px-3 py-2">Status</th>
                    <th className="border-b px-3 py-2">Outcome</th>
                    <th className="border-b px-3 py-2">AI Reviewed</th>
                    <th className="border-b px-3 py-2">Human Reviewed</th>
                    <th className="border-b px-3 py-2">Duration (s)</th>
                    <th className="border-b px-3 py-2">Executed at</th>
                    <th className="border-b px-3 py-2 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {campaign.calls.map((call) => (
                    <tr key={call.id} className="text-sm">
                      <td className="border-b px-3 py-2 font-medium">
                        {call.contact?.name || call.contact_id}
                      </td>
                      <td className="border-b px-3 py-2">
                        <span
                          className={`rounded-full px-2 py-1 text-xs font-medium ${CALL_DIRECTION_BADGES[call.direction]}`}
                        >
                          {call.direction}
                        </span>
                      </td>
                      <td className="border-b px-3 py-2">
                        <span
                          className={`rounded-full px-2 py-1 text-xs font-medium ${CALL_STATUS_BADGES[call.status]}`}
                        >
                          {call.status}
                        </span>
                      </td>
                      <td className="border-b px-3 py-2">
                        {call.outcome ? (
                          <span
                            className={`rounded-full px-2 py-1 text-xs font-medium ${outcomeDisplay.badgeClass(call.outcome)}`}
                          >
                            {outcomeDisplay.label(call.outcome)}
                          </span>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </td>
                      <td className="border-b px-3 py-2">
                        {call.is_reviewed ? (
                          <span className="rounded-full bg-purple-100 px-2 py-1 text-xs font-medium text-purple-800">
                            Verified
                          </span>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </td>
                      <td className="border-b px-3 py-2">
                        <input
                          type="checkbox"
                          checked={call.human_reviewed || false}
                          onChange={(e) => {
                            updateHumanReviewedMutation.mutate({
                              id: call.id,
                              humanReviewed: e.target.checked,
                            });
                          }}
                          disabled={updateHumanReviewedMutation.isPending}
                          className="text-primary focus:ring-primary h-4 w-4 cursor-pointer rounded border-gray-300 disabled:cursor-not-allowed disabled:opacity-50"
                        />
                      </td>
                      <td className="border-b px-3 py-2">{call.duration}</td>
                      <td className="border-b px-3 py-2">{formatDateTime(call.executed_at)}</td>
                      <td className="border-b px-3 py-2 text-right">
                        <Button variant="secondary" size="sm" asChild>
                          <Link to={`/calls/${call.id}`}>
                            <Eye className="h-4 w-4" />
                            View
                          </Link>
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
